import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { Menu, X } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import logoImage from '@/assets/logo.png';

export default function Header() {
  const { user, profile, signOut } = useAuth();
  const { language, setLanguage, t } = useLanguage();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const isActive = (path: string) => location.pathname === path;
  const isAdmin = profile?.role === 'admin' || profile?.role === 'dev';
  const isVendor = profile?.role === 'vendor';

  const navLinks = [
    { path: '/search', label: t('nav.solutions') },
    { path: '/platforms', label: t('nav.platforms') },
    { path: '/blog', label: t('nav.blog') },
    { path: '/insights', label: t('nav.insights') },
    { path: '/about', label: t('nav.about') },
    { path: '/contact', label: t('nav.contact') },
  ];

  return (
    <header className="sticky top-0 z-50 bg-background/95 backdrop-blur-sm border-b border-border">
      <div className="container mx-auto px-6 h-16 flex items-center justify-between">
        {/* Logo */}
        <Link to="/" className="flex items-center gap-2">
          <img src={logoImage} alt="Med AI Explorer" className="h-8 w-auto" />
          <span className="text-lg font-semibold tracking-tight">
            <span className="text-gradient">Med AI</span>
            <span className="text-foreground"> Explorer</span>
          </span>
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-1">
          {navLinks.map((link) => (
            <Link 
              key={link.path}
              to={link.path} 
              className={`px-4 py-2 text-sm font-medium transition-colors rounded-lg ${
                isActive(link.path) 
                  ? 'text-foreground bg-muted' 
                  : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
              }`}
            >
              {link.label}
            </Link>
          ))}
        </nav>

        {/* Right Section */}
        <div className="flex items-center gap-3">
          {/* Language Switcher */}
          <div className="hidden sm:flex items-center bg-muted rounded-lg p-0.5">
            <button
              onClick={() => setLanguage('fr')}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-all ${
                language === 'fr' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              FR
            </button>
            <button
              onClick={() => setLanguage('en')}
              className={`px-2.5 py-1.5 text-xs font-medium rounded-md transition-all ${
                language === 'en' 
                  ? 'bg-background text-foreground shadow-sm' 
                  : 'text-muted-foreground hover:text-foreground'
              }`}
            >
              EN
            </button>
          </div>

          {user ? (
            <div className="hidden md:flex items-center gap-2">
              {isAdmin && (
                <Link to="/admin">
                  <Button variant="ghost" size="sm" className="text-sm font-medium text-primary">
                    Admin
                  </Button>
                </Link>
              )}
              {isVendor && (
                <Link to="/vendor">
                  <Button variant="ghost" size="sm" className="text-sm font-medium">
                    {profile?.company_name || t('vendor.dashboard')}
                  </Button>
                </Link>
              )}
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={handleSignOut} 
                className="text-sm text-muted-foreground hover:text-foreground"
              >
                {t('nav.logout')}
              </Button>
            </div>
          ) : (
            <Link to="/auth" className="hidden md:block">
              <Button size="sm" className="text-sm font-medium bg-gradient-primary hover:opacity-90 transition-opacity">
                {t('nav.vendorSpace')}
              </Button>
            </Link>
          )}

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden p-2 text-muted-foreground hover:text-foreground"
          >
            {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border bg-background">
          <nav className="container mx-auto px-6 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link 
                key={link.path}
                to={link.path} 
                onClick={() => setMobileMenuOpen(false)}
                className={`px-4 py-3 text-sm font-medium transition-colors rounded-lg ${
                  isActive(link.path) 
                    ? 'text-foreground bg-muted' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
                }`}
              >
                {link.label}
              </Link>
            ))}
            
            <div className="divider my-2" />
            
            {/* Mobile Language Switcher */}
            <div className="flex items-center gap-2 px-4 py-2">
              <span className="text-sm text-muted-foreground">Langue:</span>
              <div className="flex items-center bg-muted rounded-lg p-0.5">
                <button
                  onClick={() => setLanguage('fr')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    language === 'fr' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground'
                  }`}
                >
                  FR
                </button>
                <button
                  onClick={() => setLanguage('en')}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    language === 'en' 
                      ? 'bg-background text-foreground shadow-sm' 
                      : 'text-muted-foreground'
                  }`}
                >
                  EN
                </button>
              </div>
            </div>
            
            {user ? (
              <>
                {isAdmin && (
                  <Link 
                    to="/admin" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 text-sm font-medium text-primary rounded-lg hover:bg-muted/50"
                  >
                    Admin
                  </Link>
                )}
                {isVendor && (
                  <Link 
                    to="/vendor" 
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 text-sm font-medium rounded-lg hover:bg-muted/50"
                  >
                    {profile?.company_name || t('vendor.dashboard')}
                  </Link>
                )}
                <button 
                  onClick={() => { handleSignOut(); setMobileMenuOpen(false); }}
                  className="px-4 py-3 text-sm font-medium text-left text-muted-foreground rounded-lg hover:bg-muted/50"
                >
                  {t('nav.logout')}
                </button>
              </>
            ) : (
              <Link 
                to="/auth" 
                onClick={() => setMobileMenuOpen(false)}
                className="mx-4 mt-2"
              >
                <Button className="w-full text-sm font-medium bg-gradient-primary">
                  {t('nav.vendorSpace')}
                </Button>
              </Link>
            )}
          </nav>
        </div>
      )}
    </header>
  );
}
